'use es6';

import { BOGUS_BR } from '../utils/dom';
import { getContentOfSelection, isNestedOnlyChild, clearRngContent, isNestedLastChild } from './utils';

// Handle block formatting of the selection within a codeblock/pre tag.
// This splits the block into {before} & {after} code blocks and inserts the
// originally selected content as a new block of the selected {format}.
export function initCodeblockPlugin(editor) {
  editor.addCommand('hscodeblock', (cmd, {
    format,
    applyFormatDefault
  }) => {
    const selectedNode = editor.selection.getNode();
    const rng = editor.selection.getRng();
    let parentCodeblock = editor.dom.getParent(selectedNode, 'pre');

    // Selection spans past & outside the codeblock, so treat the entire block as selected
    if (!parentCodeblock) {
      parentCodeblock = editor.dom.getParent(rng.startContainer, 'pre');
      rng.setStartBefore(parentCodeblock);
      editor.selection.setRng(rng);
      applyFormatDefault();
      return;
    }

    // Execute the original formatter.apply if the selection is the entire code block
    if (selectedNode === parentCodeblock && selectedNode.firstChild === rng.startContainer && selectedNode.lastChild === rng.endContainer || selectedNode !== parentCodeblock && editor.selection.isCollapsed() && isNestedOnlyChild(selectedNode, parentCodeblock)) {
      applyFormatDefault();
      return;
    }
    let bookmark;
    if (rng.collapsed) {
      const parentBlock = editor.dom.getParent(rng.startContainer, 'strong,em,a,span:not([data-mce-type="bookmark"])', parentCodeblock);
      if (parentBlock) {
        editor.selection.select(parentBlock);
      } else {
        // Treat entire codeblock as selection unless the cursor is on the
        // the last empty line
        bookmark = editor.selection.getBookmark();
        const {
          previousSibling,
          nextSibling
        } = editor.dom.get(`${bookmark.id}_start`);
        if (previousSibling && previousSibling.nodeName === 'BR' && nextSibling && nextSibling.nodeName === 'BR') {
          // Occasionally there is an empty text node after the last <br>
          if (isNestedLastChild(nextSibling, parentCodeblock) || nextSibling.nextSibling && nextSibling.nextSibling.nodeName === '#text' && nextSibling.nextSibling.nodeValue === '' && isNestedLastChild(nextSibling.nextSibling, parentCodeblock)) {
            editor.dom.remove(previousSibling);
          }
        } else {
          editor.selection.moveToBookmark(bookmark);
          editor.selection.select(parentCodeblock);
          applyFormatDefault();
          return;
        }
      }
    }
    bookmark = bookmark || editor.selection.getBookmark();
    const bookmarkNodes = {
      getStart: () => editor.dom.get(`${bookmark.id}_start`),
      getEnd: () => editor.dom.get(`${bookmark.id}_end`) || editor.dom.get(`${bookmark.id}_start`)
    };
    const bookmarkStart = bookmarkNodes.getStart();
    let bookmarkEnd = bookmarkNodes.getEnd();
    const clonedCodeblock = parentCodeblock.cloneNode(true);
    const content = getContentOfSelection(editor, rng, bookmarkStart, bookmarkEnd);
    const afterContent = getContentOfSelection(editor, rng, bookmarkEnd, parentCodeblock.lastChild);

    // Clear content of and after the selection
    clearRngContent(editor, rng, bookmarkStart, parentCodeblock.lastChild);

    // Create new format block with the old content
    const newBlock = editor.dom.create(format, {}, content || BOGUS_BR);
    editor.dom.insertAfter(newBlock, parentCodeblock);

    // Remove previous codeblock if empty
    if (!parentCodeblock.childNodes.length || parentCodeblock.childNodes.length === 1 && parentCodeblock.childNodes[0].nodeName === '#text' && parentCodeblock.childNodes[0].nodeValue === '') {
      parentCodeblock.parentNode.removeChild(parentCodeblock);
    }

    // Insert cloned codeblock with content before and of the selection cleared
    if (afterContent !== '' && afterContent !== '&nbsp;') {
      editor.dom.insertAfter(clonedCodeblock, newBlock);
      bookmarkEnd = bookmarkNodes.getEnd();
      if (bookmarkEnd.nextSibling && bookmarkEnd.nextSibling.nodeName === '#text' && bookmarkEnd.nextSibling.nodeValue === '') {
        bookmarkEnd = bookmarkEnd.nextSibling;
      }
      if (bookmarkEnd.nextSibling && bookmarkEnd.nextSibling.nodeName === 'BR') {
        bookmarkEnd = bookmarkEnd.nextSibling;
      }
      clearRngContent(editor, rng, clonedCodeblock.firstChild, bookmarkEnd);
    }
    editor.selection.setCursorLocation(newBlock, newBlock.childNodes.length);
  });
}